/**
 * Hook to create an ABR filter.
 *
 * Follows the same refetch pattern as useCreateCommunityPolicy.
 */

import { useMutation } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import type { AbrFilter, AbrFilterCriteriaInput } from '@moderation/domain/AbrFilter';
import { CREATE_ABR_FILTER, GET_ABR_FILTERS } from '@moderation/graphql';
import { useCallback, useMemo, useState } from 'react';

interface CreateFilterParams {
  name: string;
  description?: string;
  criteria: AbrFilterCriteriaInput;
}

interface UseCreateAbrFilterResult {
  createFilter: (params: CreateFilterParams) => Promise<AbrFilter | null>;
  loading: boolean;
  errors: DomainError[] | null;
  resetErrors: () => void;
}

export function useCreateAbrFilter(communityId: string): UseCreateAbrFilterResult {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  const [executeMutation, { loading }] = useMutation(CREATE_ABR_FILTER, {
    refetchQueries: communityId ? [{ query: GET_ABR_FILTERS, variables: { communityId } }] : [],
    awaitRefetchQueries: true,
  });

  const createFilter = useCallback(
    async (params: CreateFilterParams): Promise<AbrFilter | null> => {
      try {
        setErrors(null);

        track('CreateAbrFilterInitiated', { communityId });

        const result = await executeMutation({
          variables: {
            input: {
              communityId,
              name: params.name,
              description: params.description,
              criteria: params.criteria,
            },
          },
        });

        const created = result.data?.createAbrFilter ?? null;

        if (created) {
          track('CreateAbrFilterSucceeded', { communityId });
        } else {
          track('CreateAbrFilterFailed', { communityId, errorMessage: 'No data returned' });
        }

        return created;
      } catch (error) {
        track('CreateAbrFilterFailed', {
          communityId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return null;
      }
    },
    [executeMutation, communityId]
  );

  const resetErrors = useCallback(() => setErrors(null), []);

  return useMemo(() => ({ createFilter, loading, errors, resetErrors }), [createFilter, loading, errors, resetErrors]);
}
